import { api } from "@signet/web-kit";

/* ──────────────────────────────────────────────────────────────────────────
   What the Signet-styled reporting surfaces read: the connections page and
   the home cards. The legacy page has its own requests in legacy/data.ts.

   The shapes mirror modules/reporting/server. The web half never imports the
   server half, so they are written out by hand.
   ────────────────────────────────────────────────────────────────────────── */

export const BASE = "/api/m/reporting";
/** The nightly Infloww export, as an owner sees it. Never the tokens. */
export type InflowwStatus = {
  configured: boolean;
  updatedAt: string | null;
  schedule: { hour: number; minute: number; timeZone: string };
  /** When the session lapses, and days from now, from the refresh token. */
  expiresAt: string | null;
  expiresInDays: number | null;
  runs: { date: string; status: "running" | "complete" | "failed"; attempts: number; startedAt: string; finishedAt: string | null; error: string | null; exports: string[] }[];
};

export type Settings = {
  google: { configured: boolean; problem: string | null; source: "env" | "stored" | "missing"; serviceAccount: string | null };
  sheets: Record<string, { value: string; source: "env" | "setting" | "default"; label: string }>;
  ai: { configured: boolean };
};

export const keys = {
  all: ["reporting"] as const,
  settings: ["reporting", "settings"] as const,
  infloww: ["reporting", "infloww"] as const,
};

export const fetchInfloww = () => api<InflowwStatus>(`${BASE}/infloww`);
export const fetchSettings = () => api<Settings>(`${BASE}/settings`);

/* ── numbers ───────────────────────────────────────────────────────────── */

/** Money the way reporting has always shown it: whole units, the workspace's currency. */
export function money(value: number | null | undefined, currency: string | null | undefined = "USD", cents = false): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "";
  try {
    return new Intl.NumberFormat("en-AU", { style: "currency", currency: currency || "USD", currencyDisplay: "narrowSymbol", maximumFractionDigits: cents ? 2 : 0, minimumFractionDigits: cents ? 2 : 0 }).format(value);
  } catch {
    return `$${value.toFixed(cents ? 2 : 0)}`;
  }
}

/** Percent change, or null when there is nothing to compare to. Never a fake zero. */
export function change(current: number | null | undefined, previous: number | null | undefined): number | null {
  if (current === null || current === undefined || previous === null || previous === undefined || previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function shortDate(iso: string | null | undefined, withYear = false): string {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return `${d} ${MONTHS[(m ?? 1) - 1]}${withYear ? ` ${y}` : ""}`;
}
